/**
 * Sidebar.jsx
 * Brand mark, primary navigation, and the signed-in account.
 * Screens that aren't built yet carry the phase that delivers them, so the
 * shell never pretends to be finished.
 *
 * On a phone (below 900px, see mobile.css) the same sidebar is a drawer,
 * opened from the app bar or the tab bar and closed by the scrim, the close
 * button, Escape, or going to another screen.
 *
 * Each group collapses. Twenty-six links in one column is a column nobody
 * reads to the end of, so only the group you are working in is open; the rest
 * are a heading each, and what you last had open is remembered.
 */

import { useEffect, useRef, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  Calendar,
  Chart,
  Chat,
  Chevron,
  Clock,
  Close,
  Crop,
  Database,
  Download,
  Edit,
  Eye,
  Fanout,
  Film,
  Hash,
  Help,
  Home,
  Image,
  Layers,
  Link,
  Megaphone,
  Plus,
  Pulse,
  Recycle,
  Send,
  Settings,
  Sparkle,
  Table,
  Target,
  Tag,
  Users,
} from './Icons.jsx';

/**
 * The navigation, in groups.
 *
 * A flat list of eighteen items is a list nobody reads — the eye gives up
 * around seven. The groups are the four questions actually being asked when
 * someone reaches for the sidebar: what am I making, who is talking to me, how
 * did it do, and what is this app set up with.
 *
 * Dashboard sits outside them all, because it is where you land.
 */
const GROUPS = [
  {
    // No heading, so nothing to collapse: this is where you land.
    key: 'home',
    title: null,
    links: [{ to: '/', label: 'Dashboard', icon: Home, end: true }],
  },
  {
    key: 'make',
    title: 'Make',
    links: [
      { to: '/copilot', label: 'Copilot', icon: Sparkle },
      { to: '/create', label: 'Create', icon: Plus },
      { to: '/video', label: 'Video', icon: Film },
      { to: '/repurpose', label: 'Repurpose', icon: Fanout },
      { to: '/drafts', label: 'Drafts', icon: Edit },
      { to: '/campaigns', label: 'Campaigns', icon: Layers },
      { to: '/calendar', label: 'Calendar', icon: Calendar },
      { to: '/autoschedule', label: 'Auto-schedule', icon: Clock },
      { to: '/evergreen', label: 'Evergreen', icon: Recycle },
      { to: '/import', label: 'Import', icon: Table },
    ],
  },
  {
    // One entry, not four. Comments, Messages, Auto-reply and Approvals all
    // answered the same question — who is waiting on me — and splitting it
    // four ways is how a DM sat unanswered while somebody watched Comments.
    // They are tabs on the Inbox screen now.
    key: 'respond',
    title: 'Respond',
    links: [{ to: '/inbox', label: 'Inbox', icon: Chat, badge: 'inbox' }],
  },
  {
    key: 'measure',
    title: 'Measure',
    links: [
      { to: '/analytics', label: 'Analytics', icon: Chart },
      { to: '/predict', label: 'Content check', icon: Target },
      { to: '/website', label: 'Website', icon: Download },
      { to: '/listening', label: 'Listening', icon: Pulse },
      { to: '/competitors', label: 'Competitors', icon: Eye },
      { to: '/hashtags', label: 'Hashtags', icon: Hash },
      { to: '/ads', label: 'Ads', icon: Megaphone },
      { to: '/costs', label: 'Costs', icon: Database },
    ],
  },
  {
    key: 'setup',
    title: 'Set up',
    links: [
      { to: '/brands', label: 'Brands', icon: Tag },
      { to: '/library', label: 'Library', icon: Image },
      { to: '/resize', label: 'Resize & convert', icon: Crop },
      { to: '/bio', label: 'Link in bio', icon: Link },
    ],
  },
  {
    // These four used to be pinned to the bottom, which cost about 190px of
    // permanent height in a sidebar that already could not show a third of
    // itself on a laptop. They scroll with everything else now; only the
    // account chip stays pinned, because that is identity rather than
    // navigation.
    key: 'account',
    title: 'Account',
    links: [
      { to: '/team', label: 'Team', icon: Users },
      { to: '/export', label: 'Export', icon: Download },
      { to: '/help', label: 'Help & Support', icon: Help },
      { to: '/settings', label: 'Settings', icon: Settings },
    ],
  },
];

const OPEN_KEY = 'kreeboom.nav.open';

/**
 * Which group a path belongs to, so landing on a screen opens its group.
 *
 * Longest match wins: /drafts and / both prefix-match a draft's URL, and only
 * the longer one is the group actually being visited.
 *
 * @param {string} path - The current pathname
 * @returns {string|null} The group key, or null if nothing matches
 */
function groupFor(path) {
  let best = null;
  let longest = -1;

  for (const group of GROUPS) {
    for (const link of group.links) {
      const hit = link.end ? path === link.to : path === link.to || path.startsWith(`${link.to}/`);
      if (hit && link.to.length > longest) {
        longest = link.to.length;
        best = group.key;
      }
    }
  }

  return best;
}

/**
 * What was open last time, or — first visit — the group you are in.
 *
 * Dashboard is its own headingless group, so arriving there matches nothing
 * that can be opened and the whole sidebar would read as five closed
 * headings. Make opens instead: it is the reason people are here.
 *
 * @param {string} path - The current pathname
 * @returns {Record<string, boolean>}
 */
function readOpen(path) {
  try {
    const saved = JSON.parse(localStorage.getItem(OPEN_KEY));
    if (saved && typeof saved === 'object') return saved;
  } catch {
    // Corrupt or blocked storage is not worth a broken sidebar.
  }

  const here = groupFor(path);
  const titled = GROUPS.find((group) => group.key === here && group.title);
  return { [titled ? titled.key : 'make']: true };
}

/**
 * @param {string|null} user - The signed-in email
 * @param {{comments: number, messages: number}} waiting - Replies owed, from useWaiting
 * @param {boolean} open - Whether the phone drawer is open
 * @param {() => void} onClose - Closes the phone drawer
 */
export default function Sidebar({ user, waiting, open, onClose }) {
  const navRef = useRef(null);
  const here = useLocation().pathname;

  // Which groups are expanded, by group key. Remembered across visits.
  const [openGroups, setOpenGroups] = useState(() => readOpen(here));

  const toggleGroup = (key) =>
    setOpenGroups((was) => {
      const next = { ...was, [key]: !was[key] };
      try {
        localStorage.setItem(OPEN_KEY, JSON.stringify(next));
      } catch {
        // Private mode: the sidebar still works, it just forgets.
      }
      return next;
    });

  // Arriving on a screen whose group is collapsed — from a link on another
  // page, the tab bar, or a bookmark — opens that group, so the highlighted
  // row is never hidden inside a heading.
  useEffect(() => {
    const key = groupFor(here);
    // Dashboard's group has no heading and is always shown; nothing to open.
    if (!key || !GROUPS.some((group) => group.key === key && group.title)) return;
    setOpenGroups((was) => (was[key] ? was : { ...was, [key]: true }));
  }, [here]);

  /**
   * Whether anything is hidden below the fold.
   *
   * Twenty-six items do not fit a laptop: on a 1080p screen six hundred pixels
   * of this menu sit below the edge, and the overlay scrollbar most browsers
   * use stays invisible until you happen to scroll it. A whole screen was
   * reported missing for exactly that reason. The fade is the only thing
   * telling anyone there is more.
   */
  const [more, setMore] = useState(false);

  useEffect(() => {
    const nav = navRef.current;
    if (!nav) return undefined;

    const check = () =>
      setMore(nav.scrollHeight - nav.scrollTop - nav.clientHeight > 4);

    check();
    nav.addEventListener('scroll', check, { passive: true });

    // The list changes height when a badge appears, and the window changes
    // height whenever it likes.
    const observer = new ResizeObserver(check);
    observer.observe(nav);

    return () => {
      nav.removeEventListener('scroll', check);
      observer.disconnect();
    };
  }, []);

  // Landing on a screen whose link is below the fold should not look like the
  // link is missing.
  //
  // Deferred a frame: on first mount the sidebar has not been laid out yet,
  // the nav's height is still zero, and scrolling into view is a no-op against
  // a box with no scroll in it.
  useEffect(() => {
    const frame = requestAnimationFrame(() => {
      navRef.current
        ?.querySelector('.nav__link.is-active')
        ?.scrollIntoView({ block: 'nearest' });
    });
    return () => cancelAnimationFrame(frame);
  }, [here, openGroups]);

  return (
    <>
      <div className={`sidebar-scrim${open ? ' is-open' : ''}`} onClick={onClose} aria-hidden="true" />

      <aside id="sidebar" className={`sidebar${open ? ' is-open' : ''}`}>
        <button type="button" className="icon-btn sidebar__close" onClick={onClose} aria-label="Close menu">
          <Close />
        </button>

        <NavLink to="/" className="logo" aria-label="Kreeboom home">
          {/* Two lockups, one per theme: each has its own baked-in background,
              so the wrong one on the wrong theme shows a visible slab. */}
          <img className="logo__img logo__img--dark" src="/logo-dark.png" alt="Kreeboom" />
          <img className="logo__img logo__img--light" src="/logo-light.png" alt="Kreeboom" />
        </NavLink>

        <nav className={`nav${more ? ' has-more' : ''}`} ref={navRef}>
          {GROUPS.map((group) => {
            // An untitled group has no heading to click, so it is always open.
            const isOpen = !group.title || openGroups[group.key] === true;

            // A collapsed group would otherwise swallow the count on Inbox —
            // the one number in here that means someone is waiting — so it
            // moves up to the heading while the group is shut.
            const owed = group.links.reduce(
              (sum, link) => sum + (link.badge ? waiting[link.badge] || 0 : 0),
              0,
            );

            return (
              <div className={`nav__group${isOpen ? ' is-open' : ''}`} key={group.key}>
                {group.title && (
                  <button
                    type="button"
                    className="nav__title"
                    onClick={() => toggleGroup(group.key)}
                    aria-expanded={isOpen}
                    aria-controls={`nav-group-${group.key}`}
                  >
                    <span className="nav__title-text">{group.title}</span>
                    {!isOpen && owed > 0 && (
                      <span className="nav__count" aria-label={`${owed} waiting for a reply`}>
                        {owed > 99 ? '99+' : owed}
                      </span>
                    )}
                    <Chevron size={14} />
                  </button>
                )}

                <div id={`nav-group-${group.key}`} className="nav__links" hidden={!isOpen}>
                  {group.links.map(({ to, label, icon: Icon, end, badge }) => (
                    <NavLink
                      key={to}
                      to={to}
                      end={end}
                      className={({ isActive }) => `nav__link${isActive ? ' is-active' : ''}`}
                    >
                      <Icon />
                      {label}
                      {badge && waiting[badge] > 0 && (
                        <span className="nav__count" aria-label={`${waiting[badge]} waiting for a reply`}>
                          {waiting[badge] > 99 ? '99+' : waiting[badge]}
                        </span>
                      )}
                    </NavLink>
                  ))}
                </div>
              </div>
            );
          })}
        </nav>

        <div className="sidebar__foot">
          <div className="user-chip" title={user || 'Not signed in'}>
            <span className="avatar">K</span>
            <span style={{ minWidth: 0 }}>
              KreeBoom
              <span className="user-chip__email" style={{ display: 'block' }}>
                {user || '—'}
              </span>
            </span>
            <Chevron size={16} />
          </div>
        </div>
      </aside>
    </>
  );
}
